import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { History, Loader2, CheckCircle2, XCircle, RefreshCw, ArrowUpDown, Ban } from 'lucide-react';
import api from '../../utils/api';
import TableNavigation from '../TableNavigation';

/**
 * ExportJobsLog — PT-#64.
 *
 * Historial PERSISTENTE de exportaciones del cliente actual, debajo de la caja de EXPORTAR.
 * Lista los últimos jobs (hora, duración, pedido por, cantidad, tomas, estado, archivo), el
 * más nuevo arriba, con paginador estilo Publicaciones (TableNavigation arriba y abajo,
 * default 20). Incluye:
 *   - Toggle on/off del logging (PUT /export/settings) — "editor on-off".
 *   - Selector de orden de la lista — "selector de orden de páginas".
 *
 * El backend (server/routes/exportRoutes.js) persiste cada export en `export_jobs` (MariaDB
 * por-cliente) de forma NON-FATAL: si el logging está off o falla, la exportación no se ve
 * afectada. Este panel es sólo lectura del historial + el switch de settings.
 */

interface ExportJobsLogProps {
  /** Se bumpea tras una exportación exitosa para refrescar la lista. */
  refreshKey?: number;
}

interface ExportJob {
  id: number;
  job_id: string | null;
  email: string | null;
  filename: string | null;
  toma_count: number | null;
  row_count: number | null;
  estimated_total: number | null;
  status: string;
  error: string | null;
  duration_ms: number | null;
  started_at: string | null;
  finished_at: string | null;
}

const PAGE_SIZE_OPTIONS = [20, 50, 100];

// Opciones del selector de orden (mapean a ?sort=&dir= — whitelisteados en el backend).
const SORT_OPTIONS: { value: string; sort: string; dir: 'asc' | 'desc'; labelKey: string; fallback: string }[] = [
  { value: 'started_desc', sort: 'started_at', dir: 'desc', labelKey: 'exportLog.sort_newest', fallback: 'Más nuevas primero' },
  { value: 'started_asc', sort: 'started_at', dir: 'asc', labelKey: 'exportLog.sort_oldest', fallback: 'Más viejas primero' },
  { value: 'rows_desc', sort: 'row_count', dir: 'desc', labelKey: 'exportLog.sort_rows', fallback: 'Más registros' },
  { value: 'duration_desc', sort: 'duration_ms', dir: 'desc', labelKey: 'exportLog.sort_slowest', fallback: 'Más lentas' },
];

/** Fecha ISO → "DD/MM/YYYY HH:mm" (es-AR, 24h). Convención de fechas del repo. */
function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
}

/** ms → "Xs" o "Xm Ys" (tiempo transcurrido del export). */
function formatDuration(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${s}s`;
}

const ExportJobsLog: React.FC<ExportJobsLogProps> = ({ refreshKey = 0 }) => {
  const { t } = useTranslation();

  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortValue, setSortValue] = useState('started_desc');
  const [loading, setLoading] = useState(false);
  const [loggingEnabled, setLoggingEnabled] = useState(true);
  const [savingSetting, setSavingSetting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fetchJobs = useCallback(async () => {
    const opt = SORT_OPTIONS.find(o => o.value === sortValue) || SORT_OPTIONS[0];
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(pageIndex * pageSize),
        sort: opt.sort,
        dir: opt.dir,
      });
      const res = await api.get(`/api/export/jobs?${params.toString()}`);
      setJobs(Array.isArray(res.data?.jobs) ? res.data.jobs : []);
      setTotal(typeof res.data?.total === 'number' ? res.data.total : 0);
    } catch (e) {
      console.error('[ExportJobsLog] error cargando historial:', e);
      setJobs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pageIndex, pageSize, sortValue]);

  // Settings (on/off) al montar.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/export/settings');
        if (!cancelled) setLoggingEnabled(res.data?.loggingEnabled !== false);
      } catch { /* default ON */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Lista: al montar, al cambiar página/orden y tras cada export (refreshKey).
  useEffect(() => { fetchJobs(); }, [fetchJobs, refreshKey]);

  const toggleLogging = useCallback(async () => {
    const next = !loggingEnabled;
    setLoggingEnabled(next); // optimista
    setSavingSetting(true);
    try {
      await api.put('/api/export/settings', { loggingEnabled: next });
    } catch (e) {
      console.error('[ExportJobsLog] error guardando settings:', e);
      setLoggingEnabled(!next); // revertir
    } finally {
      setSavingSetting(false);
    }
  }, [loggingEnabled]);

  const statusBadge = (job: ExportJob) => {
    if (job.status === 'done') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
          <CheckCircle2 className="h-3.5 w-3.5" /> {t('exportLog.status_done', 'Completado')}
        </span>
      );
    }
    if (job.status === 'error') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700" title={job.error || undefined}>
          <XCircle className="h-3.5 w-3.5" /> {t('exportLog.status_error', 'Error')}
        </span>
      );
    }
    // PT-#64.b: job trabado auto-cancelado por el reaper (backend reiniciado / crash).
    if (job.status === 'canceled' || job.status === 'cancelled') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500" title={job.error || undefined}>
          <Ban className="h-3.5 w-3.5" /> {t('exportLog.status_canceled', 'Cancelado')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('exportLog.status_running', 'En curso')}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header: título + toggle on/off + selector de orden + refrescar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-pink-500" />
          <h3 className="text-base font-semibold text-gray-800">
            {t('exportLog.title', 'Historial de exportaciones')}
          </h3>
          <span className="text-xs text-gray-400 tabular-nums">
            ({total.toLocaleString('es-AR')})
          </span>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Selector de orden */}
          <div className="flex items-center gap-1.5">
            <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
            <select
              value={sortValue}
              onChange={(e) => { setSortValue(e.target.value); setPageIndex(0); }}
              className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>
              ))}
            </select>
          </div>

          {/* Toggle on/off del logging */}
          <button
            type="button"
            onClick={toggleLogging}
            disabled={savingSetting}
            className="flex items-center gap-2 text-xs text-gray-600 disabled:opacity-50"
            title={t('exportLog.toggle_hint', 'Activar o desactivar el registro de exportaciones')}
          >
            <span>{t('exportLog.logging', 'Registro')}</span>
            <span
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                loggingEnabled ? 'bg-pink-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  loggingEnabled ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </span>
          </button>

          {/* Refrescar */}
          <button
            type="button"
            onClick={fetchJobs}
            disabled={loading}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            title={t('exportLog.refresh', 'Refrescar')}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Paginador arriba */}
      <TableNavigation
        id="pag-export-log-top"
        position="top"
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalRows={total}
        totalPages={totalPages}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={(i) => setPageIndex(Math.max(0, Math.min(i, totalPages - 1)))}
        onPageSizeChange={(s) => { setPageSize(s); setPageIndex(0); }}
      />

      {/* Tabla */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium text-gray-500 border-b border-gray-200">
              <th className="px-4 py-2 whitespace-nowrap">{t('exportLog.col_time', 'Hora')}</th>
              <th className="px-4 py-2 whitespace-nowrap">{t('exportLog.col_status', 'Estado')}</th>
              <th className="px-4 py-2 whitespace-nowrap">{t('exportLog.col_by', 'Pedido por')}</th>
              <th className="px-4 py-2 whitespace-nowrap text-right">{t('exportLog.col_rows', 'Registros')}</th>
              <th className="px-4 py-2 whitespace-nowrap text-right">{t('exportLog.col_tomas', 'Tomas')}</th>
              <th className="px-4 py-2 whitespace-nowrap text-right">{t('exportLog.col_duration', 'Duración')}</th>
              <th className="px-4 py-2">{t('exportLog.col_file', 'Archivo')}</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">
                  {t('exportLog.empty', 'Todavía no hay exportaciones registradas.')}
                </td>
              </tr>
            )}
            {jobs.map((job) => (
              <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 whitespace-nowrap tabular-nums text-gray-700">{formatDateTime(job.started_at)}</td>
                <td className="px-4 py-2 whitespace-nowrap">{statusBadge(job)}</td>
                <td className="px-4 py-2 whitespace-nowrap text-gray-600">{job.email || '—'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums text-gray-700">
                  {job.row_count != null
                    ? job.row_count.toLocaleString('es-AR')
                    : (job.estimated_total != null ? `~${job.estimated_total.toLocaleString('es-AR')}` : '—')}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums text-gray-700">
                  {job.toma_count != null ? job.toma_count.toLocaleString('es-AR') : '—'}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums text-gray-700">{formatDuration(job.duration_ms)}</td>
                <td className="px-4 py-2 text-gray-600 max-w-[280px] truncate" title={job.filename || undefined}>
                  {job.filename || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginador abajo */}
      <TableNavigation
        id="pag-export-log-bottom"
        position="bottom"
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalRows={total}
        totalPages={totalPages}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={(i) => setPageIndex(Math.max(0, Math.min(i, totalPages - 1)))}
        onPageSizeChange={(s) => { setPageSize(s); setPageIndex(0); }}
      />
    </div>
  );
};

export default ExportJobsLog;
